// @ts-strict-ignore
import { IProjectHelper } from '@project/helper/i-project.helper';
import { Connection } from '@3d-scene/model3d/connection';
import { TextureAreaBorderParameters } from '@3d-scene/model/texture-area-border-parameters';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { SelectionArticleGroupIdentity } from '@project/components/article/model/vo/selection-article-group-identity';
import { ProjectModel } from '@project/model/project.model';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { UserModel } from '@user/model/user.model';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { UserVO } from '@user/model/vo/user.vo';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { ProjectUtils } from '../components/project/util/project-utils';
import { Injectable } from '@angular/core';
import { ProjectModelService } from '../service/project-model.service';
import { ProjectArticleService } from '../service/project-article.service';
import { ProjectMetaElementService } from '../manager/project-meta-element.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { IOperation } from '@shared/undo/operation/i-operation';
import { SequenceOperation } from '@shared/undo/operation/sequence-operation';

@Injectable({ providedIn: 'root' })
export class DefaultProjectHelper implements IProjectHelper {
  private readonly DEFAULT_TEXTURE_BORDER_COLOR: number = 0x000000;

  private _showWireFramesForHiddenArticles: boolean = false;

  public get showWireFramesForHiddenArticles(): boolean {
    return this._showWireFramesForHiddenArticles;
  }

  public set showWireFramesForHiddenArticles(value: boolean) {
    this._showWireFramesForHiddenArticles = value;
  }

  constructor(
    protected userModel: UserModel,
    protected projectModel: ProjectModel,
    protected projectModelService: ProjectModelService,
    protected projectArticleService: ProjectArticleService,
    protected projectMetaElementService: ProjectMetaElementService,
    protected undoRedoHelper: UndoRedoHelper,
    protected alertHelper: AlertHelper,
    protected compositeArticleUtils: CompositeArticleUtils,
    protected containerService: ContainerService,
    protected labelingUtils: LabelingUtils,
    protected articleContainerUtils: ArticleContainerUtils,
  ) {}

  public createSelection(articleUsage: ArticleUsageVO): SelectionArticleGroupIdentity {
    if (this.projectModel.isAdminView) {
      return new SelectionArticleGroupIdentity(null, [articleUsage.id]);
    }

    let articleUsageIds: string[] | undefined = undefined;

    if (this.isWireBridgeArticle(articleUsage)) {
      articleUsageIds = this.projectArticleService.collectGroupArticleUsageIds(articleUsage);
    } else if (ProjectUtils.canRemoveCodingFingerinFreeMountingOrMCS(this.projectModel)) {
      articleUsageIds = [articleUsage.id];
    } else {
      articleUsageIds = this.projectArticleService.collectGroupArticleUsageIds(articleUsage);
    }

    const selectionGroupIdentity: SelectionArticleGroupIdentity = new SelectionArticleGroupIdentity(articleUsage.groupId, articleUsageIds);

    return selectionGroupIdentity;
  }

  private isWireBridgeArticle(articleUsage: ArticleUsageVO) {
    const articleContainer = this.containerService.findArticleContainerInGlobal(articleUsage.id);
    let isWireBridgeArticle = ArticleUtils.isWireBridgeArticle(articleContainer?.article);
    return isWireBridgeArticle;
  }

  public isCompositePartArticleModificationAllowed(articleContainer: ArticleContainer): boolean {
    return this.compositeArticleUtils.isCompositePartArticleModificationAllowed(articleContainer);
  }

  public isCompositePartArticleSelectionAllowed(articleContainer: ArticleContainer): boolean {
    return this.compositeArticleUtils.isCompositePartArticleSelectionAllowed(articleContainer);
  }

  public createTextureAreaBorder(connection: Connection): TextureAreaBorderParameters {
    return this.createTextureAreaBorderByDefault(connection);
  }

  public isMoveArticleAllowed(articleContainer: ArticleContainer): boolean {
    return true;
  }

  public isDeleteCompositePartArticleAllowed(articleContainer: ArticleContainer): boolean {
    return this.isCompositePartArticleModificationAllowed(articleContainer);
  }

  protected isStrippingComplexArticleRequired(): boolean {
    return false;
  }

  public isCopyPasteAllowed(): boolean {
    return true;
  }

  protected createTextureAreaBorderByDefault(connection: Connection): TextureAreaBorderParameters {
    if (!connection) {
      return null;
    }
    const articleContainer: ArticleContainer = connection.articleContainer;
    if (!articleContainer) {
      return null;
    }
    let textureBorderParameters: TextureAreaBorderParameters = null;
    if (ArticleUtils.isTextBorderAvailable(articleContainer.article)) {
      textureBorderParameters = this.createDefaultTextureBorderParameters();
    }
    return textureBorderParameters;
  }

  protected createDefaultTextureBorderParameters(): TextureAreaBorderParameters {
    const textureBorderParameters: TextureAreaBorderParameters = new TextureAreaBorderParameters();
    textureBorderParameters.borderColor = this.DEFAULT_TEXTURE_BORDER_COLOR;
    textureBorderParameters.drawOuterBorder = true;
    textureBorderParameters.forceDrawBorder = false;
    return textureBorderParameters;
  }

  public isInitialRotationRequired(articleContainer: ArticleContainer): boolean {
    return false;
  }

  public isReplaceWithDifferentWidthAllowed(): boolean {
    return true;
  }

  public considerOriginalAppliedRotationForReplacement(): boolean {
    return true;
  }

  public async deleteArticles(articleUsageIds: string[], deleteFunction: (articleUsageIdsToRemove: string[]) => Promise<SequenceOperation>): Promise<void> {
    if (this.isPresetArticle(articleUsageIds)) {
      this.alertHelper.showAlert('global.label.delete', 'main.message.delete_drillinghole_from_preset', {}, [AlertButton.OK], "info");
      return;
    }

    if (this.existsNotDeletableArticles(articleUsageIds)) {
      this.alertHelper.showAlert('global.label.delete', 'main.message.delete_not_deletable', {}, [AlertButton.OK], "info");
      return;
    }

    const notDeletableCompositeUsageIds: string[] = this.findNotDeletableCompositeArticleUsageIds(articleUsageIds);
    if (notDeletableCompositeUsageIds.length > 0) {
      ArrayUtils.mergeArrays(articleUsageIds, this.collectArticlesWithAllCompositePartArticlesToRemove(notDeletableCompositeUsageIds));
      if (!this.containerService.isDeletable(articleUsageIds, this.projectMetaElementService, this.userModel)) {
        this.alertHelper.showAlert('global.label.delete', 'main.message.delete_not_deletable_composite', {}, [AlertButton.OK], "info");
      } else {
        const answer = await this.alertHelper.showAlert('global.label.delete', 'main.message.delete_entire_composite_article', {},
          [AlertButton.OK, AlertButton.CANCEL], "help");
        if (answer === AlertButton.OK) {
          await this.proceedDelete(articleUsageIds, deleteFunction);
        }
      }
    } else if (!this.currentUserCanDeleteLabelingArticles(articleUsageIds)) {
      this.alertHelper.showAlert('global.label.delete', 'main.message.delete_not_default_labeling', {}, [AlertButton.OK], "info");
    } else {
      await this.proceedDelete(articleUsageIds, deleteFunction);
    }
  }

  public currentUserCanDeleteLabelingArticles(articleUsageIds: string[]): boolean {
    const articleUsageIdsToDelete: string[] = this.containerService.collectForwardConnectedArticleUsageIds(articleUsageIds);
    const articleContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(articleUsageIdsToDelete);
    return ArrayUtils.isEmpty(articleContainers) ? true : this.labelingUtils.userCanDeleteLabelingArticles(this.userModel.user, articleContainers);
  }

  protected async proceedDelete(articleUsageIds: string[], deleteFunction: (articleUsageIdsToRemove: string[]) => Promise<SequenceOperation>): Promise<void> {
    const undoOperation: IOperation = await deleteFunction(articleUsageIds);
    if (undoOperation) {
      await this.undoRedoHelper.pushUndoOperation(undoOperation);
    }
  }

  protected isPresetArticle(articleUsageIds: string[]): boolean {
    if (ArrayUtils.isEmpty(articleUsageIds)) {
      return false;
    }
    for (const articleUsageId of articleUsageIds) {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsageId);
      if (!articleContainer) {
        continue;
      }
      if (ArticleUtils.isDrillingHole(articleContainer.article)) {
        const mountingRail: ArticleContainer = this.articleContainerUtils.findMountingRailToDrillingHole(articleContainer);
        if (!mountingRail) {
          continue;
        }
        const hasDrillingHolePreset: boolean = ArticleUsageUtils.getArticleUsageInfoPropertyBooleanValue(
          mountingRail.articleUsage,
          ArticleUsageInfoPropertyDef.HAS_DRILLINGHOLE_PRESET
        );
        if (hasDrillingHolePreset) {
          return true;
        }
      }
    }
    return false;
  }

  protected existsNotDeletableArticles(articleUsageIds: string[]): boolean {
    return !this.containerService.isDeletable(articleUsageIds, this.projectMetaElementService, this.userModel);
  }

  protected findNotDeletableCompositeArticleUsageIds(articleUsageIds: string[]): string[] {
    if (ArrayUtils.isEmpty(articleUsageIds)) {
      return [];
    }

    const compositeNotDeletableUsageIds: string[] = [];
    const articleUsageIdsToDelete: string[] = this.containerService.collectForwardConnectedArticleUsageIds(articleUsageIds);
    const processedCompositePartUsageIds: string[] = [];
    for (const articleUsageId of articleUsageIdsToDelete) {
      if (processedCompositePartUsageIds.indexOf(articleUsageId) >= 0) {
        continue;
      }
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsageId);
      const compositeUsageId: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(
        articleContainer,
        this.isStrippingComplexArticleRequired()
      );
      if (!compositeUsageId || this.isDeleteCompositePartArticleAllowed(articleContainer)) {
        continue;
      }

      const compositePartArticleUsageIds: string[] = this.containerService.getCompositePartArticleUsageIds(
        articleContainer,
        this.isStrippingComplexArticleRequired()
      );
      ArrayUtils.mergeArrays(processedCompositePartUsageIds, compositePartArticleUsageIds);
      if (
        !this.compositeArticleUtils.groupContainsAllCompositePartArticlesConnectedToMountingRail(
          articleUsageIdsToDelete,
          articleContainer,
          this.isStrippingComplexArticleRequired()
        ) &&
        !this.compositeArticleUtils.isAllCompositePartsIncluded(compositePartArticleUsageIds, articleUsageIdsToDelete)
      ) {
        compositeNotDeletableUsageIds.push(compositeUsageId);
      }
    }
    return compositeNotDeletableUsageIds;
  }

  protected collectArticlesWithAllCompositePartArticlesToRemove(compositeUsageIds: string[]): string[] {
    const articleUsageIdsToDelete: string[] = [];
    for (const compositeUsageId of compositeUsageIds) {
      const compositePartIds: string[] = this.projectModel
        .getCompositePartArticleUsagesById(compositeUsageId, this.isStrippingComplexArticleRequired())
        .map((usage) => usage.id);
      ArrayUtils.mergeArrays(articleUsageIdsToDelete, compositePartIds);
    }
    return articleUsageIdsToDelete;
  }

  public isRotateArticleAllowed(articleContainer: ArticleContainer): boolean {
    return true;
  }

  public isDisplayOfProjectStructureEnabled(user: UserVO = null): boolean {
    return true;
  }

  public isLabelingCorrectionDisabled(): boolean {
    return false;
  }
}
